/**
 * CENTRALIZED ORDER CALCULATION SELECTORS
 *
 * This file contains all order calculation logic using Redux selectors.
 * Benefits:
 * - Single source of truth for calculations
 * - Memoization via Reselect - calculations only run when dependencies change
 * - No need for useMemo in components
 * - Composable and testable
 *
 * @requires reselect
 */

import { createSelector } from 'reselect';

// ============================================================================
// BASE SELECTORS - Read directly from Redux store
// ============================================================================

/**
 * Select all denominations from store
 * @param {Object} state - Redux state
 * @returns {Array} Array of denomination objects
 */
const selectDenominations = (state) => state.Denominations?.allData || [];


/**
 * Select user wallet balance from store
 * @param {Object} state - Redux state
 * @returns {number} User's wallet balance
 */
const selectUserBalance = (state) => state.UserBalance?.allData || 0;


// ============================================================================
// MEMOIZED SELECTORS - Cached calculations
// ============================================================================

/**
 * Select only denominations with quantity >= 1
 * Memoized - only recalculates when denominations array changes
 */
export const selectFilteredItems = createSelector(
  [selectDenominations],
  (items) => {
    const filtered = items.filter(item => (item?.QUANTITY || 0) >= 1);
    return filtered;
  }
);

/**
 * Calculate total gross value (before discount)
 * Formula: Σ (AMOUNT × QUANTITY)
 * Memoized - only recalculates when filtered items change
 */
export const selectTotalValue = createSelector(
  [selectFilteredItems],
  (items) => items.reduce(
    (sum, item) => sum + (item.AMOUNT || 0) * (item.QUANTITY || 0),
    0
  )
);

/**
 * Calculate total discount amount
 * Formula: Σ GROSS_TOTAL_DISCOUNT
 * Each denomination's GROSS_TOTAL_DISCOUNT is pre-calculated in reducer as:
 * (quantity × amount × discount%) / 100
 *
 * Memoized - only recalculates when filtered items change
 */
export const selectTotalDiscount = createSelector(
  [selectFilteredItems],
  (items) => items.reduce(
    (sum, item) => sum + (item.GROSS_TOTAL_DISCOUNT || 0),
    0
  )
);

/**
 * Calculate net payable (after discount, before wallet deduction)
 * Formula: Total Value - Total Discount
 * Memoized - only recalculates when totalValue or totalDiscount changes
 */
export const selectNetPayable = createSelector(
  [selectTotalValue, selectTotalDiscount],
  (totalValue, totalDiscount) => totalValue - totalDiscount
);

/**
 * Calculate final net payable (after discount and wallet deduction)
 * Formula: Max(Net Payable - User Balance, 0)
 * Memoized - only recalculates when netPayable or userBalance changes
 */
export const selectNetPayableWithBalance = createSelector(
  [selectNetPayable, selectUserBalance],
  (netPayable, userBalance) => Math.max(netPayable - userBalance, 0)
);


export const selectTotalCashback = createSelector(
  [selectFilteredItems],
  (items) =>
    items.reduce(
      (sum, item) => sum + (item.CASHBACK_AMOUNT || 0),
      0
    )
);

/**
 * Get complete order summary with all calculated values
 * Returns object with all financial calculations for UI display
 * Memoized - only recalculates when any dependency changes
 *
 * @returns {Object} Complete order summary
 * @property {number} totalValue - Gross total before discount
 * @property {number} totalDiscount - Total discount amount
 * @property {number} netPayable - Net amount after discount
 * @property {number} netPayableWithBalance - Final amount after wallet deduction
 * @property {Array} filteredItems - Items with quantity >= 1
 */
export const selectOrderSummary = createSelector(
  [
    selectTotalValue,
    selectTotalDiscount,
    selectTotalCashback,
    selectNetPayable,
    selectNetPayableWithBalance,
    selectFilteredItems
  ],
  (
    totalValue,
    totalDiscount,
    totalCashback,
    netPayable,
    netPayableWithBalance,
    filteredItems
  ) => ({
    totalValue,
    totalDiscount,
    totalCashback,
    netPayable,
    netPayableWithBalance,
    filteredItems,
    itemCount: filteredItems.length
  })
);

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/*
// In any component:
import { useSelector } from 'react-redux';
import { selectOrderSummary, selectNetPayableWithBalance } from './selectors';

// Example 1: Get complete summary
const summary = useSelector(selectOrderSummary);

// Example 2: Get specific value
const netPayable = useSelector(selectNetPayableWithBalance);

// BENEFITS:
// ✅ No useMemo needed in component
// ✅ Calculation runs only once even if multiple components use it
// ✅ Automatically recalculates only when denominations change
// ✅ Super fast - O(1) lookup for cached values
*/

// export default {
//     selectFilteredItems,
//     selectTotalValue,
//     selectTotalDiscount,
//     selectNetPayable,
//     selectNetPayableWithBalance,
//     selectOrderSummary
// };
